import { memo, useState } from 'react'

import { DevicesOther, ImportantDevices } from '@mui/icons-material'
import { ListItemText, styled } from '@mui/material'
import { useMatchRoute } from '@tanstack/react-router'
import { motion } from 'motion/react'
import { useTranslation } from 'react-i18next'

import { Collapse, collapseAnimationFactory } from '@shared/components/Collapse'
import { ListItemButtonLink } from 'components/LinkedMUIComponents'
import {
  NavLinkListItem,
  NavLinkListItemTextPendable,
  NavLinkStartIcon,
  SectionDivider,
  SectionHeader,
} from 'domains/_Sidebar'
import { NavigationList } from 'layouts'
import { collectionsOverviewRoute, collectionsRoute } from 'pages/NWPlatform/Collections'
import { expAppsOverviewRoute } from 'pages/NWPlatform/ExpApps'

const SectionCollapse = styled(Collapse)({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  overflow: 'hidden',
})

type NavigationProps = {
  organizationId: string
  environmentId: string
}

const NavigationRaw = ({ organizationId, environmentId }: NavigationProps) => {
  const { t } = useTranslation(['common', 'entity', 'domain'])

  const matchRoute = useMatchRoute()

  const [collapsed, setCollapsed] = useState(false)

  return (
    <>
      <SectionHeader
        title={t('domain:_Sidebar.Environment.ExpApplications.sectionTitle')}
        collapsed={collapsed}
        setCollapsed={(state) => setCollapsed(state)}
      />

      <SectionCollapse collapsed={collapsed} component="li">
        <NavigationList component={motion.ul} variants={collapseAnimationFactory({ opacity: 0 }, { opacity: 1 })}>
          <NavLinkListItem disablePadding component={motion.li} whileHover={{ translateX: 2 }}>
            <ListItemButtonLink
              to={collectionsOverviewRoute.to}
              params={{ organizationId, environmentId }}
              selected={!!matchRoute({ to: collectionsRoute.to, fuzzy: true })}
            >
              <NavLinkStartIcon>
                <ImportantDevices />
              </NavLinkStartIcon>
              <ListItemText
                disableTypography
                primary={
                  <NavLinkListItemTextPendable>
                    {t('domain:_Sidebar.Environment.ExpApplications.showcaseApp')}
                  </NavLinkListItemTextPendable>
                }
              />
            </ListItemButtonLink>
          </NavLinkListItem>

          <NavLinkListItem disablePadding component={motion.li} whileHover={{ translateX: 2 }}>
            <ListItemButtonLink
              to={expAppsOverviewRoute.to}
              params={{ organizationId, environmentId }}
              selected={!!matchRoute({ to: expAppsOverviewRoute.to, fuzzy: true })}
            >
              <NavLinkStartIcon>
                <DevicesOther />
              </NavLinkStartIcon>
              <ListItemText
                disableTypography
                primary={
                  <NavLinkListItemTextPendable>
                    {t('domain:_Sidebar.Environment.ExpApplications.customApp', { count: 2 })}
                  </NavLinkListItemTextPendable>
                }
              />
            </ListItemButtonLink>
          </NavLinkListItem>

          <SectionDivider />
        </NavigationList>
      </SectionCollapse>
    </>
  )
}

export const Navigation = memo(NavigationRaw)
